/**
 * HHC.FR — Barre compacte à deux paliers : 59 € livraison offerte, 89 € 10 g offerts.
 *
 * Affiche, dans le cart drawer de l'application Cartly / Spurit OCC3 et sur la
 * page panier native `/cart`, la progression du panier vers les deux paliers.
 *
 * STRICTEMENT PASSIF ET INFORMATIF : ne modifie jamais le panier, n'ajoute ni
 * ne retire aucun produit, ne touche à aucune remise, à aucun tarif et à aucune
 * règle. Shopify Shipping reste seul juge de la livraison offerte au checkout ;
 * EG Auto Add to Cart reste seul responsable du cadeau. Le palier 59 € n'est
 * qu'un repère visuel : aucune règle de livraison n'est reproduite ici.
 *
 * Base de calcul — réplique exacte d'EG (`Xr`/`Vr`, useFinalPrice = true) :
 *     Σ ( final_price × quantity ) sur les lignes SANS propriété `_Gifted`.
 * La ligne cadeau étant exclue, l'ajout du cadeau ne peut pas faire repasser
 * le panier sous le seuil (pas de boucle possible).
 *
 * UN SEUL MONTANT, UNE SEULE PROGRESSION : le même montant éligible alimente
 * les deux paliers. La barre est graduée sur le palier haut (89 €) ; le palier
 * 59 € est un marqueur fixe à 5900/8900 ≈ 66,29 %. Il n'existe pas de seconde
 * fonction de calcul, ni de seconde barre.
 *
 * DEUX MODES, DÉCIDÉS PAR LE SERVEUR :
 *   - avec `data-shipping-threshold` (France) : deux paliers, deux marqueurs ;
 *   - sans (BE / DE / IT / ES) : palier cadeau seul, un libellé, un marqueur.
 * Ce script ne connaît PAS le pays et ne le lit nulle part : il constate la
 * présence ou l'absence du seuil livraison dans la configuration rendue par
 * Liquid. Un changement de pays passe par le sélecteur Shopify, qui recharge la
 * page — la configuration revient à jour sans aucune requête d'ici.
 *
 * DEUX POINTS DE RENDU, UNE SEULE LOGIQUE :
 *   - le cart drawer Cartly (`.spurit-occ3__slide-drawer`) ;
 *   - la page panier native `/cart`, via les conteneurs `[data-hhc-gpb-mount]`
 *     rendus par le même snippet Liquid.
 * `computeEligible()`, les seuils, les textes et la carte sont partagés : rien
 * n'est dupliqué entre les deux rendus.
 *
 * NE PERTURBE JAMAIS EG (incident du 2026-08-18) :
 *   - le composant n'émet AUCUNE requête réseau, jamais ;
 *   - les appels privés d'EG (`_easygift_internal`, `dp=true`, émis par sa
 *     routine « duplicate cart ») sont totalement ignorés : aucun état, aucun
 *     rendu, aucune requête ;
 *   - le wrapper `fetch` est en OBSERVATION PURE : il retourne la promesse
 *     d'origine intacte et ne lit qu'un `response.clone()`.
 *
 * OBSERVATION REDONDANTE (bug de synchronisation du 2026-08-19) :
 * `window.fetch` est un point d'accroche PARTAGÉ et mutable. Sur cette page,
 * EG l'enveloppe, Cartly l'enveloppe, et EG expose en plus
 * `window.__restoreBuyNowInterceptor`, qui REMET `window.fetch` et
 * `XMLHttpRequest.prototype.open/send` aux références qu'il avait captées —
 * évinçant silencieusement tout observateur installé après lui. Une partie du
 * trafic panier passe d'ailleurs par XMLHttpRequest, qu'EG et Cartly
 * enveloppent tous les deux. Une observation unique sur `fetch` est donc
 * fragile : une fois perdue, le composant reste figé sur sa dernière valeur
 * alors que le drawer, lui, continue de fonctionner.
 *
 * D'où TROIS observations indépendantes, aucune n'émettant quoi que ce soit :
 *   1. `window.fetch`, ré-armé s'il a été remplacé ;
 *   2. `XMLHttpRequest`, en lecture seule ;
 *   3. les évènements panier que Cartly publie déjà sur `window`.
 * Toutes convergent vers le MÊME `isCartResponseUrl()` (donc le même
 * `EG_PRIVATE`) et le MÊME `absorb()` : une seule décision, un seul calcul.
 *
 * Performance : aucune dépendance, aucun polling, aucun setInterval, aucun
 * setTimeout, aucun MutationObserver, aucune requête ajoutée. Les données
 * proviennent uniquement des réponses panier que Cartly, EG et Horizon
 * émettent déjà, et de la valeur serveur portée par le point de montage.
 */
(() => {
  'use strict';

  const config = document.getElementById('hhc-gift-progress-bar-config');
  if (!config) return;

  const THRESHOLD = parseInt(config.dataset.threshold, 10);
  if (!Number.isFinite(THRESHOLD) || THRESHOLD <= 0) return;

  /* Palier bas, purement informatif. Défini dans le snippet, jamais ici.
     ABSENT = mode « cadeau seul » : le snippet n'émet cet attribut que si la
     localisation storefront est la France. Une valeur aberrante est traitée
     comme une absence — jamais comme une raison de ne rien afficher : le palier
     cadeau, lui, doit rester visible partout. */
  const SHIPPING_RAW = parseInt(config.dataset.shippingThreshold, 10);
  const HAS_SHIPPING_TIER = Number.isFinite(SHIPPING_RAW) && SHIPPING_RAW > 0 && SHIPPING_RAW < THRESHOLD;
  const SHIPPING_THRESHOLD = HAS_SHIPPING_TIER ? SHIPPING_RAW : null;

  const GIFT_PROPERTY = config.dataset.giftProperty || '_Gifted';
  const MONEY_FORMAT = config.dataset.moneyFormat || '{{amount}}';
  const AMOUNT_TOKEN = config.dataset.amountToken || '__HHC_GPB_AMOUNT__';
  const TIER_SHIPPING = config.dataset.tierShipping || '';
  const TIER_GIFT = config.dataset.tierGift || '';
  const STATE_SHIPPING = config.dataset.stateShipping || '';
  const STATE_GIFT = config.dataset.stateGift || '';
  const STATE_DONE = config.dataset.stateDone || '';
  const ARIA_LABEL = config.dataset.ariaLabel || '';

  function iconMarkup(name) {
    const tpl = config.querySelector('[data-hhc-gpb-icon-' + name + ']');
    return tpl ? tpl.innerHTML : '';
  }

  const CARD_SELECTOR = '[data-hhc-gpb-card]';
  const MOUNT_SELECTOR = '[data-hhc-gpb-mount]';
  const DRAWER_SELECTOR = '.spurit-occ3__slide-drawer';
  const ANCHOR_SELECTORS = [
    '.spurit-occ3__total-savings',
    '.spurit-occ3__discounts-row',
    '.spurit-occ3__subtotal-container',
    '.spurit-occ3__subtotal',
  ];

  let eligibleCents = parseInt(config.dataset.initialEligible, 10) || 0;
  let lastAbsorbAt = 0;

  /* Fenêtre pendant laquelle un panier déjà capté est considéré comme frais. */
  const FRESH_MS = 500;
  const now = () => (window.performance && window.performance.now ? window.performance.now() : Date.now());

  /* ------------------------------------------------------------------ */
  /* Formatage monétaire : système Shopify `shop.money_format`.          */
  /* ------------------------------------------------------------------ */

  function withDelimiters(cents, precision, thousands, decimal) {
    const value = (Number(cents) / 100).toFixed(precision);
    const parts = value.split('.');
    const integer = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    return parts[1] ? integer + decimal + parts[1] : integer;
  }

  /** Typographie FR : espace insécable avant le symbole (« 77,48 € »). */
  function withCurrencySpace(text) {
    return text.replace(/(\d)\s*([€$£¥])/g, '$1\u00a0$2');
  }

  function applyFormat(cents, format) {
    return withCurrencySpace(format.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, name) => {
      switch (name) {
        case 'amount':
          return withDelimiters(cents, 2, ',', '.');
        case 'amount_no_decimals':
          return withDelimiters(cents, 0, ',', '.');
        case 'amount_with_comma_separator':
          return withDelimiters(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withDelimiters(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return withDelimiters(cents, 2, "'", '.');
        case 'amount_no_decimals_with_apostrophe_separator':
          return withDelimiters(cents, 0, "'", '.');
        case 'amount_with_space_separator':
          return withDelimiters(cents, 2, ' ', ',');
        case 'amount_no_decimals_with_space_separator':
          return withDelimiters(cents, 0, ' ', ',');
        default:
          return match;
      }
    }));
  }

  function formatMoney(cents) {
    return applyFormat(cents, MONEY_FORMAT);
  }

  /* Variante sans décimales, dérivée du `money_format` de la boutique.
     Utilisée UNIQUEMENT pour l'affichage des seuils (« 59 € », « 89 € »
     plutôt que « 59,00 € ») : ce sont des repères marketing, pas des prix
     produit. Les valeurs fonctionnelles restent en centimes, inchangées. */
  const MONEY_FORMAT_NO_DECIMALS = MONEY_FORMAT.replace(
    /\{\{\s*amount(_with_comma_separator|_with_apostrophe_separator|_with_space_separator)?\s*\}\}/g,
    (match, separator) => '{{amount_no_decimals' + (separator || '') + '}}'
  );

  /** Sans décimales si le montant tombe juste, format complet sinon. */
  function formatMoneyRounded(cents) {
    return cents % 100 === 0 ? applyFormat(cents, MONEY_FORMAT_NO_DECIMALS) : formatMoney(cents);
  }

  /** Remplace le jeton de montant d'un libellé traduit. */
  function fillAmount(template, cents) {
    return template.split(AMOUNT_TOKEN).join(formatMoneyRounded(cents));
  }

  /* ------------------------------------------------------------------ */
  /* Calcul — réplique de la logique d'EG.                               */
  /* ------------------------------------------------------------------ */

  function isCartPayload(data) {
    return !!data && typeof data === 'object' && Array.isArray(data.items) && typeof data.item_count === 'number';
  }

  function computeEligible(cart) {
    return cart.items.reduce((sum, item) => {
      if (!item) return sum;
      const quantity = parseInt(item.quantity, 10);
      if (!Number.isFinite(quantity) || quantity <= 0) return sum;
      const properties = item.properties;
      if (properties && properties[GIFT_PROPERTY]) return sum;
      const unit = typeof item.final_price === 'number' ? item.final_price : item.price;
      if (!Number.isFinite(unit)) return sum;
      return sum + unit * quantity;
    }, 0);
  }

  /* ------------------------------------------------------------------ */
  /* Rendu — idempotent, sans duplication ni clignotement.               */
  /* ------------------------------------------------------------------ */

  /* Position du marqueur « livraison » sur une barre graduée sur le palier
     haut. Dérivée des deux seuils : aucune valeur en dur. */
  const SHIPPING_POSITION = HAS_SHIPPING_TIER ? SHIPPING_THRESHOLD / THRESHOLD : null;

  function buildTier(name, position) {
    const tier = document.createElement('span');
    tier.className = 'hhc-gift-progress-bar__tier';
    tier.setAttribute('data-hhc-gpb-tier', name);

    const icon = document.createElement('span');
    icon.className = 'hhc-gift-progress-bar__tier-icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.innerHTML = iconMarkup(name);

    const label = document.createElement('span');
    label.className = 'hhc-gift-progress-bar__tier-label';
    label.textContent = fillAmount(name === 'shipping' ? TIER_SHIPPING : TIER_GIFT, position);

    tier.appendChild(icon);
    tier.appendChild(label);
    return tier;
  }

  function buildMarker(name, position) {
    const marker = document.createElement('span');
    marker.className = 'hhc-gift-progress-bar__marker';
    marker.setAttribute('data-hhc-gpb-marker', name);
    marker.style.setProperty('--hhc-gpb-pos', String(position));
    return marker;
  }

  function buildNode() {
    const node = document.createElement('div');
    node.className = 'hhc-gift-progress-bar';
    node.setAttribute('data-hhc-gpb-card', '');

    /* Rangée 1 — les paliers actifs, libellés depuis les seuils du snippet.
       En mode cadeau seul, le palier livraison n'est pas rendu du tout : pas de
       nœud caché, pas de libellé, rien à masquer en CSS. */
    const tiers = document.createElement('div');
    tiers.className = 'hhc-gift-progress-bar__tiers';
    if (HAS_SHIPPING_TIER) tiers.appendChild(buildTier('shipping', SHIPPING_THRESHOLD));
    tiers.appendChild(buildTier('gift', THRESHOLD));

    /* Rangée 2 — UNE seule barre, graduée sur le palier haut, deux marqueurs. */
    const bar = document.createElement('div');
    bar.className = 'hhc-gift-progress-bar__bar';
    bar.setAttribute('data-hhc-gpb-bar', '');
    bar.setAttribute('role', 'progressbar');
    bar.setAttribute('aria-valuemin', '0');
    bar.setAttribute('aria-valuemax', '100');
    if (ARIA_LABEL) bar.setAttribute('aria-label', ARIA_LABEL);

    const track = document.createElement('div');
    track.className = 'hhc-gift-progress-bar__track';

    const fill = document.createElement('span');
    fill.className = 'hhc-gift-progress-bar__fill';
    fill.setAttribute('data-hhc-gpb-fill', '');
    track.appendChild(fill);

    bar.appendChild(track);
    if (HAS_SHIPPING_TIER) bar.appendChild(buildMarker('shipping', SHIPPING_POSITION));
    bar.appendChild(buildMarker('gift', 1));

    /* Rangée 3 — un seul message, le montant isolé pour être mis en gras. */
    const status = document.createElement('p');
    status.className = 'hhc-gift-progress-bar__status';
    status.setAttribute('data-hhc-gpb-status', '');

    const prefix = document.createElement('span');
    prefix.setAttribute('data-hhc-gpb-prefix', '');
    const amount = document.createElement('strong');
    amount.className = 'hhc-gift-progress-bar__amount';
    amount.setAttribute('data-hhc-gpb-amount', '');
    const suffix = document.createElement('span');
    suffix.setAttribute('data-hhc-gpb-suffix', '');

    status.appendChild(prefix);
    status.appendChild(amount);
    status.appendChild(suffix);

    node.appendChild(tiers);
    node.appendChild(bar);
    node.appendChild(status);
    return node;
  }

  function findAnchor(drawer) {
    for (const selector of ANCHOR_SELECTORS) {
      const anchor = drawer.querySelector(selector);
      if (anchor && anchor.parentNode) return anchor;
    }
    return null;
  }

  /** Drawer Cartly : insertion juste avant « Economies totales » / le sous-total. */
  function ensureInDrawer() {
    const drawer = document.querySelector(DRAWER_SELECTOR);
    if (!drawer) return null;

    let node = drawer.querySelector(CARD_SELECTOR);
    const anchor = findAnchor(drawer);

    if (!anchor) {
      if (node) node.remove();
      return null;
    }
    if (!node) node = buildNode();
    if (node.nextElementSibling !== anchor || node.parentNode !== anchor.parentNode) {
      anchor.parentNode.insertBefore(node, anchor);
    }
    return node;
  }

  /** Page panier native : un point de montage rendu par Liquid, déjà bien placé. */
  function ensureInMounts(nodes) {
    document.querySelectorAll(MOUNT_SELECTOR).forEach((mount) => {
      let node = mount.querySelector(CARD_SELECTOR);
      if (!node) {
        node = buildNode();
        mount.appendChild(node);
      }
      nodes.push(node);
    });
  }

  /** Tous les points de rendu actifs. Idempotent : jamais de doublon. */
  function placeAll() {
    const nodes = [];
    const drawerNode = ensureInDrawer();
    if (drawerNode) nodes.push(drawerNode);
    ensureInMounts(nodes);
    return nodes;
  }

  /* Valeur serveur portée par le point de montage : après un morph natif de la
     section panier, c'est la donnée la plus fraîche disponible — sans requête. */
  function adoptServerValue() {
    const mount = document.querySelector(MOUNT_SELECTOR + '[data-hhc-gpb-eligible]');
    if (!mount) return false;
    const value = parseInt(mount.getAttribute('data-hhc-gpb-eligible'), 10);
    if (!Number.isFinite(value) || value < 0) return false;
    eligibleCents = value;
    lastAbsorbAt = now();
    return true;
  }

  /* Calcul unique, partagé par tous les points de rendu.
     Les quatre premiers champs sont STRICTEMENT INCHANGÉS depuis la version
     validée en production : c'est la décision cadeau, elle ne bouge pas.
     Les deux derniers ne servent qu'au repère visuel « livraison ». */
  function computeState() {
    const eligible = Math.max(0, eligibleCents);
    return {
      eligible,
      unlocked: eligible >= THRESHOLD,
      remaining: Math.max(0, THRESHOLD - eligible),
      percent: Math.min(100, Math.max(0, (eligible / THRESHOLD) * 100)),
      shippingUnlocked: HAS_SHIPPING_TIER ? eligible >= SHIPPING_THRESHOLD : null,
      shippingRemaining: HAS_SHIPPING_TIER ? Math.max(0, SHIPPING_THRESHOLD - eligible) : null,
    };
  }

  /** Peint UN point de rendu à partir de l'état commun. */
  function paint(node, state) {
    const bar = node.querySelector('[data-hhc-gpb-bar]');
    const fill = node.querySelector('[data-hhc-gpb-fill]');
    const prefix = node.querySelector('[data-hhc-gpb-prefix]');
    const amount = node.querySelector('[data-hhc-gpb-amount]');
    const suffix = node.querySelector('[data-hhc-gpb-suffix]');
    if (!bar || !fill || !prefix || !amount || !suffix) return;

    /* Message : un seul, choisi par l'état atteint. En mode cadeau seul, la
       première branche n'existe pas et le snippet a déjà fourni le wording
       correspondant pour les deux autres. */
    let template = STATE_DONE;
    let amountCents = null;
    if (HAS_SHIPPING_TIER && !state.shippingUnlocked) {
      template = STATE_SHIPPING;
      amountCents = state.shippingRemaining;
    } else if (!state.unlocked) {
      template = STATE_GIFT;
      amountCents = state.remaining;
    }

    const parts = amountCents === null ? [template] : template.split(AMOUNT_TOKEN);
    const head = parts[0] || '';
    const amountText = amountCents === null ? '' : formatMoney(amountCents);
    const tail = parts.length > 1 ? parts.slice(1).join(AMOUNT_TOKEN) : '';

    if (prefix.textContent !== head) prefix.textContent = head;
    if (amount.textContent !== amountText) amount.textContent = amountText;
    if (suffix.textContent !== tail) suffix.textContent = tail;

    const message = head + amountText + tail;
    if (bar.getAttribute('aria-valuetext') !== message) {
      bar.setAttribute('aria-valuetext', message);
    }

    /* Ne jamais annoncer 100 % tant que le palier haut n'est pas atteint. */
    const rounded = String(state.unlocked ? 100 : Math.min(99, Math.round(state.percent)));
    if (bar.getAttribute('aria-valuenow') !== rounded) {
      bar.setAttribute('aria-valuenow', rounded);
    }

    const width = state.percent.toFixed(2) + '%';
    if (fill.style.width !== width) fill.style.width = width;

    /* Paliers et marqueurs : le vert ne dit qu'une chose, « c'est acquis ». */
    if (HAS_SHIPPING_TIER) paintTier(node, 'shipping', state.shippingUnlocked);
    paintTier(node, 'gift', state.unlocked);

    node.classList.toggle('hhc-gift-progress-bar--complete', state.unlocked);
  }

  function paintTier(node, name, reached) {
    const tier = node.querySelector('[data-hhc-gpb-tier="' + name + '"]');
    if (tier) tier.classList.toggle('hhc-gift-progress-bar__tier--reached', reached);
    const marker = node.querySelector('[data-hhc-gpb-marker="' + name + '"]');
    if (marker) marker.classList.toggle('hhc-gift-progress-bar__marker--reached', reached);
  }

  /** Monte puis met à jour TOUS les points de rendu actifs. */
  function render() {
    const nodes = placeAll();
    if (!nodes.length) return;
    const state = computeState();
    nodes.forEach((node) => paint(node, state));
  }

  function absorb(data) {
    if (!isCartPayload(data)) return false;
    eligibleCents = computeEligible(data);
    lastAbsorbAt = now();
    render();
    return true;
  }

  /* ------------------------------------------------------------------ */
  /* Capture des données panier déjà en transit (0 requête ajoutée).     */
  /* ------------------------------------------------------------------ */

  const CART_ENDPOINT = /\/cart(\.js)?(\?|$)|\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  /* INCIDENT 2026-08-18 — NE JAMAIS TOUCHER AUX APPELS INTERNES D'EG.
     Avec `useFinalPrice`, EG exécute une routine « duplicate cart » qui appelle
     `/cart/update.js?_easygift_internal=true&dp=true` et `cart.json?...&dp=true`.
     Ces requêtes lui sont strictement privées : les observer nous faisait réagir
     au milieu de son cycle et empêchait l'ajout du cadeau. Elles sont désormais
     ignorées totalement — aucun état, aucun rendu, aucune requête. */
  const EG_PRIVATE = /(^|[?&])(_easygift_internal|dp)=/i;

  function isCartResponseUrl(url) {
    if (typeof url !== 'string') return false;
    if (EG_PRIVATE.test(url)) return false;
    return CART_ENDPOINT.test(url);
  }

  /* --- 1. `window.fetch`, en observation pure, ré-armable ------------- */

  let ourFetch = null;

  function armFetchObserver() {
    if (typeof window.fetch !== 'function' || window.fetch === ourFetch) return;
    /* Ce qui est en place devient l'aval : on s'ajoute à la chaîne existante,
       on n'en retire jamais personne. */
    const downstream = window.fetch.bind(window);
    ourFetch = function (...args) {
      const promise = downstream(...args);
      try {
        const input = args[0];
        const url = typeof input === 'string' ? input : input && input.url;
        if (isCartResponseUrl(url)) {
          promise
            .then((response) => {
              if (!response || !response.ok) return;
              response
                .clone()
                .json()
                .then((data) => absorb(data))
                .catch(() => {});
            })
            .catch(() => {});
        }
      } catch (error) {
        /* ne jamais casser la requête d'origine */
      }
      return promise;
    };
    window.fetch = ourFetch;
  }

  /* --- 2. `XMLHttpRequest`, en LECTURE SEULE -------------------------- */

  /* Aucune requête n'est créée ici : on se contente d'écouter `load` sur les
     requêtes que d'autres émettent déjà, exactement comme pour `fetch`, et de
     leur appliquer le même filtre d'URL. Ré-armable pour la même raison. */

  let ourXhrOpen = null;
  let ourXhrSend = null;

  function armXhrObserver() {
    const proto = window.XMLHttpRequest && window.XMLHttpRequest.prototype;
    if (!proto) return;

    if (typeof proto.open === 'function' && proto.open !== ourXhrOpen) {
      const downstream = proto.open;
      ourXhrOpen = function (method, url) {
        try {
          this.__hhcGpbUrl = typeof url === 'string' ? url : null;
        } catch (error) {
          /* certains objets sont scellés : on renonce silencieusement */
        }
        return downstream.apply(this, arguments);
      };
      proto.open = ourXhrOpen;
    }

    if (typeof proto.send === 'function' && proto.send !== ourXhrSend) {
      const downstream = proto.send;
      ourXhrSend = function () {
        try {
          this.addEventListener('load', function () {
            try {
              if (!isCartResponseUrl(this.__hhcGpbUrl)) return;
              if (this.status < 200 || this.status >= 300) return;
              const text = typeof this.responseText === 'string' ? this.responseText : '';
              if (!text) return;
              absorb(JSON.parse(text));
            } catch (error) {
              /* réponse non JSON ou illisible : on ignore */
            }
          });
        } catch (error) {
          /* ne jamais casser la requête d'origine */
        }
        return downstream.apply(this, arguments);
      };
      proto.send = ourXhrSend;
    }
  }

  /* --- 3. Les évènements panier que Cartly publie déjà ---------------- */

  /* Cartly ré-émet sur `window` chaque réponse panier qu'il observe, sur les
     DEUX transports, avec le panier dans `event.detail.response`. Le premier
     caractère de ces noms est un « с » CYRILLIQUE (U+0441) dans le bundle de
     l'application : c'est volontairement écrit en échappement ici.

     Seuls `change`, `add` et `clear` sont écoutés — jamais `update` ni
     `mutate`. Raison de sécurité : sur le transport `fetch`, Cartly ne
     transmet pas l'URL dans l'évènement, `EG_PRIVATE` ne peut donc pas être
     appliqué ; or la routine « duplicate cart » d'EG passe par
     `/cart/update.js`. Ces deux types sont donc structurellement hors de
     portée d'un état intermédiaire d'EG. */
  const CARTLY_CART_EVENTS = [
    '\u0441os-cart:change',
    '\u0441os-cart:add',
    '\u0441os-cart:clear',
  ];

  function onCartlyCartEvent(event) {
    const detail = event && event.detail;
    if (!detail) return;

    /* Transport XHR : Cartly transmet les arguments de `open()`, donc l'URL.
       On lui applique alors le même filtre que partout ailleurs. */
    const request = detail.request;
    if (Array.isArray(request) && typeof request[1] === 'string' && !isCartResponseUrl(request[1])) return;

    let data = detail.response;
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch (error) {
        return;
      }
    }
    absorb(data);
  }

  /* Ré-arme les observateurs perdus. Appelé uniquement depuis des évènements
     déjà reçus : aucun timer, aucune boucle, aucun observateur du DOM. */
  function armObservers() {
    armFetchObserver();
    armXhrObserver();
  }

  armObservers();
  CARTLY_CART_EVENTS.forEach((name) => window.addEventListener(name, onCartlyCartEvent));

  /* Page panier native Horizon : `cart-items-component` re-rend la section
     (morph) puis émet `cart:update`. Le point de montage revient alors avec une
     valeur serveur à jour — on l'adopte, sauf si une réponse panier plus récente
     a déjà été captée (le garde de fraîcheur évite d'écraser la bonne valeur). */
  document.addEventListener('cart:update', () => {
    armObservers();
    if (now() - lastAbsorbAt > FRESH_MS) adoptServerValue();
    render();
  });

  /* Cartly émet cet événement après chaque rendu de son panier (sans payload).
     On se contente de repeindre avec la dernière donnée observée : aucune
     requête n'est déclenchée ici, jamais. */
  document.addEventListener('cartly:cart-rendered', () => {
    armObservers();
    render();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', render, { once: true });
  } else {
    render();
  }
})();
